import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { 
  Table2, 
  Trash2,
  Loader2,
  FolderOpen,
  ArrowLeft,
  Plus,
  FileText,
  Building2,
  CheckCircle2,
  AlertCircle,
  Circle,
  Search,
  Download,
  Pencil,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Color mapping for different projects
const projectColors = {
  'Amgen': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200', icon: 'bg-green-500' },
  'BMS': { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200', icon: 'bg-blue-500' },
  'Hilti': { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200', icon: 'bg-orange-500' },
  'Unknown': { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200', icon: 'bg-gray-500' }
};

// Status colors for batch completion
const statusStyles = {
  'pristine': { 
    bg: 'bg-white', 
    border: 'border-gray-200',
    icon: Circle,
    iconColor: 'text-gray-400',
    label: 'Not Started',
    labelColor: 'text-gray-500'
  },
  'incomplete': { 
    bg: 'bg-red-50', 
    border: 'border-red-300',
    icon: AlertCircle,
    iconColor: 'text-red-500',
    label: 'In Progress',
    labelColor: 'text-red-600'
  },
  'complete': { 
    bg: 'bg-green-50', 
    border: 'border-green-300',
    icon: CheckCircle2,
    iconColor: 'text-green-500',
    label: 'Complete',
    labelColor: 'text-green-600'
  }
};

const ProjectsListPage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sheetToDelete, setSheetToDelete] = useState(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [sheetToRename, setSheetToRename] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);

  // Support # search
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchAggregate, setSearchAggregate] = useState(null);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchAbortRef = useRef(null);
  const searchContainerRef = useRef(null);

  const fetchSheets = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/project-sheets`);
      setSheets(response.data);
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Failed to load project sheets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSheets();
  }, []);

  // Debounced support # search
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearchAggregate(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        if (searchAbortRef.current) searchAbortRef.current.abort();
        const ctrl = new AbortController();
        searchAbortRef.current = ctrl;
        const resp = await axios.get(`${API}/project-sheets/search`, {
          params: { q },
          signal: ctrl.signal,
        });
        setSearchResults(resp.data.results || []);
        setSearchAggregate(resp.data.aggregate || null);
      } catch (err) {
        if (!axios.isCancel(err) && err.name !== "CanceledError") {
          console.error("Search error:", err);
        }
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [searchQuery]);

  // Click outside closes results dropdown
  useEffect(() => {
    const onClick = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Group sheets by project_ref
  const groupedSheets = useMemo(() => {
    const groups = {};
    sheets.forEach(sheet => {
      const projectRef = sheet.project_ref || 'Unknown';
      if (!groups[projectRef]) {
        groups[projectRef] = [];
      }
      groups[projectRef].push(sheet);
    });
    return groups;
  }, [sheets]);

  const handleDelete = async () => {
    if (!sheetToDelete) return;
    
    try {
      await axios.delete(`${API}/project-sheets/${sheetToDelete.id}`);
      setSheets(prev => prev.filter(s => s.id !== sheetToDelete.id));
      toast.success("Project sheet deleted");
    } catch (err) {
      toast.error("Failed to delete project sheet");
    } finally {
      setDeleteDialogOpen(false);
      setSheetToDelete(null);
    }
  };

  const handleToggleComplete = async (sheet) => {
    const willComplete = !sheet.manually_completed;
    const endpoint = willComplete ? "complete" : "reopen";
    try {
      await axios.post(`${API}/project-sheets/${sheet.id}/${endpoint}`);
      setSheets(prev => prev.map(s => s.id === sheet.id ? {
        ...s,
        manually_completed: willComplete,
        status: willComplete ? "complete" : s.status,
        completion_percentage: willComplete ? 100 : s.completion_percentage,
      } : s));
      toast.success(willComplete ? "Batch marked as complete" : "Batch reopened");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update batch");
    }
  };

  const openRenameDialog = (sheet) => {
    setSheetToRename(sheet);
    setRenameValue(sheet.name || "");
    setRenameDialogOpen(true);
  };

  const handleRename = async () => {
    if (!sheetToRename) return;
    const name = renameValue.trim();
    if (!name) {
      toast.error("Name cannot be empty");
      return;
    }
    if (name === sheetToRename.name) {
      setRenameDialogOpen(false);
      return;
    }
    setRenameBusy(true);
    try {
      const resp = await axios.patch(`${API}/project-sheets/${sheetToRename.id}`, { name });
      setSheets(prev => prev.map(s => s.id === sheetToRename.id ? { ...s, name: resp.data.name } : s));
      toast.success("Batch renamed");
      setRenameDialogOpen(false);
      setSheetToRename(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to rename batch");
    } finally {
      setRenameBusy(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProjectColors = (projectRef) => {
    return projectColors[projectRef] || projectColors['Unknown'];
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      <main className="flex-1 py-6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FolderOpen className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h1 className="font-heading text-2xl font-bold text-gray-900" data-testid="batch-list-title">
                    Saved Batch Sheets
                  </h1>
                  <p className="text-gray-500 mt-1">
                    Access and continue editing your saved batch sheets
                  </p>
                </div>
              </div>
              
              {isAdmin && (
                <Button 
                  variant="outline" 
                  onClick={() => navigate("/")}
                  data-testid="back-home-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              )}
            </div>
          </div>

          {/* Support # Search */}
          <div
            ref={searchContainerRef}
            className="relative bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 rounded-xl border border-blue-200 shadow-sm p-5 mb-6 animate-fade-in ring-1 ring-blue-100"
            data-testid="support-search-container"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-blue-600 text-white shadow-sm">
                <Search className="w-3.5 h-3.5" />
              </span>
              <div>
                <h3 className="font-heading text-sm font-semibold text-blue-900">Global Support &amp; Part Search</h3>
                <p className="text-xs text-blue-700/70">Find any drawing, ART # or description across every saved batch</p>
              </div>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500 pointer-events-none" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setShowResults(true); }}
                onFocus={() => setShowResults(true)}
                placeholder="Search by Drawing #, Eng #, ART # or description (e.g., PF-0027, 15-S-0014, 2268369, MT-90 OC)"
                className="pl-9 pr-10 bg-white border-blue-200 focus-visible:ring-blue-500 focus-visible:ring-offset-0 h-11 text-sm shadow-sm"
                data-testid="support-search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => { setSearchQuery(""); setSearchResults([]); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-blue-400 hover:text-blue-700 rounded transition-colors"
                  aria-label="Clear search"
                  data-testid="support-search-clear"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {showResults && searchQuery.trim().length >= 2 && (
              <div
                className="absolute z-30 left-5 right-5 top-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl max-h-96 overflow-auto"
                data-testid="support-search-results"
              >
                {searching ? (
                  <div className="p-4 flex items-center gap-2 text-gray-500 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Searching...
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500" data-testid="support-search-empty">
                    No supports found matching "{searchQuery}"
                  </div>
                ) : (
                  <>
                    {searchAggregate && (
                      <div className="px-3 py-2.5 border-b border-blue-100 bg-blue-50/60 text-xs text-blue-900 flex items-center gap-3 flex-wrap" data-testid="search-aggregate-banner">
                        <span className="font-semibold uppercase tracking-wide text-[10px] text-blue-700">
                          Across all batches
                        </span>
                        <span>
                          <strong className="font-mono">{searchAggregate.unique_drawings}</strong> drawing{searchAggregate.unique_drawings === 1 ? "" : "s"}
                        </span>
                        <span className="text-blue-300">·</span>
                        <span>
                          <strong className="font-mono">{searchAggregate.matched_rows}</strong> batch row{searchAggregate.matched_rows === 1 ? "" : "s"}
                        </span>
                        <span className="text-blue-300">·</span>
                        <span>
                          Total QTY <strong className="font-mono">{searchAggregate.total_qty}</strong>
                        </span>
                        {searchAggregate.total_length_mm > 0 && (
                          <>
                            <span className="text-blue-300">·</span>
                            <span>
                              Total length <strong className="font-mono">{searchAggregate.total_length_mm}mm</strong>
                            </span>
                          </>
                        )}
                        <button
                          type="button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const resp = await axios.get(`${API}/project-sheets/search/export`, {
                                params: { q: searchQuery.trim() },
                                responseType: "blob",
                              });
                              const url = window.URL.createObjectURL(new Blob([resp.data]));
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = `search_${searchQuery.trim().replace(/[^A-Za-z0-9_-]+/g, "_")}.xlsx`;
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                              window.URL.revokeObjectURL(url);
                            } catch (err) {
                              console.error("Export search failed:", err);
                              toast.error("Failed to export search results");
                            }
                          }}
                          className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-600 text-white text-[11px] font-medium hover:bg-blue-700 transition-colors"
                          data-testid="search-export-btn"
                        >
                          <Download className="w-3 h-3" />
                          Export Excel
                        </button>
                      </div>
                    )}
                  <ul className="divide-y divide-gray-100">
                    {searchResults.map((r, i) => {
                      const colors = getProjectColors(r.project_ref);
                      return (
                        <li
                          key={`${r.sheet_id}-${r.row_id}`}
                          className="p-3 hover:bg-blue-50 cursor-pointer flex items-start justify-between gap-3"
                          onClick={() => {
                            setShowResults(false);
                            navigate(`/projects/${r.sheet_id}?support=${encodeURIComponent(r.support_number)}`);
                          }}
                          data-testid={`support-search-result-${i}`}
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono font-semibold text-gray-900 text-sm">
                                {r.support_number}
                              </span>
                              <Badge className={`text-xs ${colors.bg} ${colors.text}`}>
                                {r.project_ref}
                              </Badge>
                              {r.fab_pack && (
                                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                  {r.fab_pack}
                                </Badge>
                              )}
                            </div>
                            <div className="mt-1 text-xs text-gray-500 flex items-center gap-3 flex-wrap">
                              <span className="flex items-center gap-1"><FolderOpen className="w-3 h-3" />{r.sheet_name}</span>
                              <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{r.extraction_filename}</span>
                              {typeof r.page_number === "number" && <span>Page {r.page_number}</span>}
                            </div>
                            {r.match_excerpt && (
                              <div className="mt-1 text-xs text-blue-700 font-mono truncate" title={r.match_excerpt}>
                                <span className="inline-block px-1.5 py-0.5 mr-1.5 rounded bg-blue-50 text-blue-700 border border-blue-200 text-[10px] uppercase tracking-wide">
                                  {r.match_reason === "art" ? "ART" : r.match_reason === "eng-drawing" ? "Eng #" : "Desc"}
                                </span>
                                {r.match_excerpt}
                                {r.match_qty > 0 && (
                                  <span className="ml-2 text-blue-500/80">· qty {Number.isInteger(r.match_qty) ? r.match_qty : r.match_qty.toFixed(1)}</span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-gray-400 flex-shrink-0">
                            {r.date_cut ? "Cut" : ""} {r.date_fabbed ? "Fabbed" : ""}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Content */}
          {loading ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-20 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          ) : sheets.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-20 text-center">
              <Table2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Batch Sheets Yet</h3>
              <p className="text-gray-500 mb-6">
                {isAdmin 
                  ? "Upload a PDF and create a batch sheet to get started"
                  : "No batches available yet. Contact your administrator."}
              </p>
              {isAdmin && (
                <Button onClick={() => navigate("/")} className="bg-blue-600 hover:bg-blue-700" data-testid="upload-pdf-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Upload PDF
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-6 animate-fade-in delay-100">
              {Object.entries(groupedSheets).map(([projectRef, projectSheets]) => {
                const colors = getProjectColors(projectRef);
                
                return (
                  <div 
                    key={projectRef}
                    className={`bg-white rounded-xl border-2 ${colors.border} shadow-sm overflow-hidden`}
                    data-testid={`project-section-${projectRef.toLowerCase()}`}
                  >
                    {/* Project Section Header */}
                    <div className={`${colors.bg} px-6 py-4 border-b ${colors.border}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 ${colors.icon} rounded-lg flex items-center justify-center`}>
                          <Building2 className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h2 className={`font-heading text-xl font-bold ${colors.text}`}>
                            {projectRef} Batches
                          </h2>
                          <p className="text-sm text-gray-500">
                            {projectSheets.length} batch{projectSheets.length !== 1 ? 'es' : ''}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Batch Sheets List */}
                    <div className="divide-y divide-gray-100">
                      {projectSheets.map((sheet, idx) => {
                        const status = statusStyles[sheet.status] || statusStyles['pristine'];
                        const StatusIcon = status.icon;
                        
                        return (
                          <div 
                            key={sheet.id}
                            className={`p-4 transition-colors cursor-pointer flex items-center justify-between gap-4 ${status.bg} hover:opacity-90 border-l-4 ${status.border}`}
                            onClick={() => navigate(`/projects/${sheet.id}`)}
                            data-testid={`batch-sheet-${projectRef.toLowerCase()}-${idx}`}
                          >
                            <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                sheet.status === 'complete' ? 'bg-green-100' : 
                                sheet.status === 'incomplete' ? 'bg-red-100' : 'bg-blue-100'
                              }`}>
                                <StatusIcon className={`w-5 h-5 ${status.iconColor}`} />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h3 className="font-semibold text-gray-900">
                                    {sheet.name}
                                  </h3>
                                  <Badge className={`text-xs ${
                                    sheet.status === 'complete' ? 'bg-green-100 text-green-700' : 
                                    sheet.status === 'incomplete' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {sheet.manually_completed ? 'Marked Complete' : status.label}
                                  </Badge>
                                </div>
                                <div className="flex flex-wrap items-center gap-2 mt-1">
                                  <span className="text-sm text-gray-500 flex items-center gap-1">
                                    <FileText className="w-3 h-3" />
                                    {sheet.extraction_filename}
                                  </span>
                                  <Badge variant="secondary" className="text-xs bg-blue-50 text-blue-700">
                                    {sheet.row_count} {sheet.sheet_type === 'hilti' ? 'parts' : 'supports'}
                                  </Badge>
                                  {sheet.completion_percentage > 0 && sheet.status !== 'complete' && (
                                    <Badge variant="outline" className="text-xs">
                                      {sheet.completion_percentage}% complete
                                    </Badge>
                                  )}
                                  {sheet.fab_packs?.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                      {sheet.fab_packs.map((fp, i) => (
                                        <Badge key={i} variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                          {fp}
                                        </Badge>
                                      ))}
                                    </div>
                                  )}
                                  {sheet.delivered_count > 0 && (
                                    <Badge className="text-xs bg-red-100 text-red-800 border-red-300 font-bold" data-testid={`delivered-badge-${idx}`}>
                                      {sheet.delivered_count} DELIVERED
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-3">
                              <div className="text-right hidden sm:block">
                                <p className="text-xs text-gray-400">Created</p>
                                <p className="text-sm text-gray-600">{formatDate(sheet.created_at)}</p>
                              </div>
                              {isAdmin && (
                                <Button
                                  variant={sheet.manually_completed ? "default" : "outline"}
                                  size="sm"
                                  className={sheet.manually_completed
                                    ? "bg-green-600 hover:bg-green-700 text-white border-green-700"
                                    : "border-green-200 text-green-700 hover:bg-green-50"
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleComplete(sheet);
                                  }}
                                  data-testid={`toggle-complete-${idx}`}
                                  title={sheet.manually_completed ? "Reopen batch" : "Mark batch as complete"}
                                >
                                  <CheckCircle2 className="w-4 h-4 sm:mr-1.5" />
                                  <span className="hidden sm:inline">
                                    {sheet.manually_completed ? "Completed" : "Mark Complete"}
                                  </span>
                                </Button>
                              )}
                              {isAdmin && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openRenameDialog(sheet);
                                  }}
                                  data-testid={`rename-sheet-${idx}`}
                                  title="Rename batch"
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              )}
                              {isAdmin && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSheetToDelete(sheet);
                                    setDeleteDialogOpen(true);
                                  }}
                                  data-testid={`delete-sheet-${idx}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <Footer />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project Sheet?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{sheetToDelete?.name}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rename Batch Dialog */}
      <AlertDialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rename Batch</AlertDialogTitle>
            <AlertDialogDescription>
              Give this batch a more recognisable name. This only affects the
              display label — the source PDF and drawings are unchanged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="my-4">
            <Input
              autoFocus
              value={renameValue}
              maxLength={120}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !renameBusy) { e.preventDefault(); handleRename(); } }}
              placeholder="Batch name"
              data-testid="rename-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={renameBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRename}
              disabled={renameBusy || !renameValue.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="rename-save-btn"
            >
              {renameBusy ? "Saving..." : "Save"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProjectsListPage;
